import { Dispatch, SetStateAction } from "react";
import { IEditFeatureDataState } from "../../../Reducers/InitialStates/EditFeatureInitialState";
import { Action, ICountry, IEditDirtyData, IEditTableData } from "../../../Types/types";
import { areStringsEqual, isCommentsError } from "../../Utils/utils";
import { COUNTRY_CODE, COUNTRY_NAME, IN_SCOPE, NOT_UPDATED, OUT_OF_SCOPE, THREE, TWO } from "../../../api/constants";
import { InewRecordOptions } from "../../../Types/types2";
import { isQtyValid } from "../../pages/ViewEditReportPage/ViewEditReportFunctions";

export const removeExistingRowIfNotErrored = (
  isCurrentRowErrored: boolean,
  isCurrentRowExistsInErrorData: boolean,
  errorRows: IEditTableData[],
  currentRowIndexInErrorData: number,
  dispatch: Dispatch<Action>,
) => {
  if (!isCurrentRowErrored && isCurrentRowExistsInErrorData) {
    errorRows.splice(currentRowIndexInErrorData, 1);
    dispatch({ type: 'setErroredRows', payload: errorRows });
  }
};
export const addNewErroredRowData = (
  isCurrentRowErrored: boolean,
  isCurrentRowExistsInErrorData: boolean,
  errorRows: IEditTableData[],
  currentRow: IEditTableData,
  masterDataRow: IEditTableData,
  dispatch: Dispatch<Action>,
  state: IEditFeatureDataState,
) => {
  if (isCurrentRowErrored && !isCurrentRowExistsInErrorData) {
    errorRows.push(currentRow);
    dispatch({ type: 'setErroredRows', payload: errorRows });
    //  entry of same data to master error data
    state.masterErroredRows.push(masterDataRow);
    dispatch({
      type: 'setMasterErroredRows',
      payload: state.masterErroredRows,
    });
  }
};

export const updateExistingRowData = (
  isCurrentRowErrored: boolean,
  isCurrentRowExistsInErrorData: boolean,
  errorRows: IEditTableData[],
  currentRowIndexInErrorData: number,
  currentRow: IEditTableData,
  dispatch: Dispatch<Action>,
) => {
  if (isCurrentRowErrored && isCurrentRowExistsInErrorData) {
    errorRows[currentRowIndexInErrorData] = currentRow;
    dispatch({ type: 'setErroredRows', payload: errorRows });
  }
};

export const checkIfCurrentRowErrored = (
  isAnyEditableColChangedExceptComments: boolean,
  isCommentsChanged: boolean,
  isCommentsEmpty: boolean,
  isQtyNotvalid: boolean,
  isDealQtyNotvalid: boolean,
  isCountryEmptyWhenInScope: boolean,
) => {
  const areQtyDealQtyCountryGood = isQtyNotvalid || isDealQtyNotvalid || isCountryEmptyWhenInScope;
  return (
    (isAnyEditableColChangedExceptComments && (!isCommentsChanged || isCommentsEmpty)) ||
    areQtyDealQtyCountryGood
  );
};

export const isColChangedExceptComments = (
  state: IEditFeatureDataState,
  QTY1: string,
  currentRow: IEditTableData,
  COMMENTS1: string,
  masterDataRow: IEditTableData,
) => {
  for (const col of state.editableCol) {
    if (col === QTY1) {
      // Normalize QTY1 values for comparison
      const currentVal = currentRow[col] === "" ? 0 : Number(currentRow[col]);
      const masterVal = Number(masterDataRow[col]);
      if (currentVal !== masterVal) {
        return true;
      }
    } else if (col.toLowerCase() !== COMMENTS1.toLowerCase() && currentRow[col] !== masterDataRow[col]) {
      return true;
    }
  }
  return false;
};

export const makeCurrentRowScopeToNumeric = (
  isScopeTableSelected: boolean,
  currentRow: IEditTableData,
  SCOPE1: string,
) => {
  // convert scope value to number else it will always error out for being different from master scope value, which is a number
  if (isScopeTableSelected && typeof currentRow[SCOPE1] === 'string') {
    currentRow[SCOPE1] = currentRow[SCOPE1] === IN_SCOPE ? 1 : 0;
  }
  return currentRow;
};

export const checkCountryEmptyWhenInScope = (
  isScopeTableSelected: boolean,
  currentRow: IEditTableData,
  SCOPE1: string,
) => {
  if (isScopeTableSelected && (currentRow[SCOPE1] === IN_SCOPE || currentRow[SCOPE1] === 1)) {
    return currentRow[COUNTRY_NAME] === '';
  }
  return false;
};

export const getCommentsQtyDealQtyValidity = (
  currentRow: IEditTableData,
  COMMENTS1: string,
  QTY1: string | number,
  DEAL_QUANTITY1: string,
  value: IEditTableData,
) => {
  return [
    Object.hasOwn(currentRow, COMMENTS1) && value[COMMENTS1] === '',
    Object.hasOwn(currentRow, QTY1) && !isQtyValid(value[QTY1]),
    Object.hasOwn(currentRow, DEAL_QUANTITY1) && !isQtyValid(value[DEAL_QUANTITY1]),
  ];
};

export const maintainErrorData = (
  value: IEditTableData,
  state: IEditFeatureDataState,
  isEditError: boolean,
  isScopeTableSelected: boolean,
  colNames: {
    COMMENTS1: string;
    QTY1: string;
    DEAL_QUANTITY1: string;
    ID1: string;
    SCOPE1: string;
  },
  dispatch: Dispatch<Action>,
) => {
  const { COMMENTS1, QTY1, DEAL_QUANTITY1, ID1, SCOPE1 } = colNames;
  let masterData: IEditTableData[] = [];
  const errorRows = [...state.erroredRows];
  let currentRow = value;
  let isCurrentRowErrored = false;
  let isCurrentRowExistsInErrorData = false;
  let currentRowIndexInErrorData = -1;
  let isCommentsChanged = false;

  const [isCommentsEmpty, isQtyNotvalid, isDealQtyNotvalid] = getCommentsQtyDealQtyValidity(
    currentRow,
    COMMENTS1,
    QTY1,
    DEAL_QUANTITY1,
    value,
  );
  if (isEditError) {
    masterData = [...state.masterErroredRows];
  } else {
    masterData = [...state.masterPaginationData];
  }

  // get the index of master data for current row
  const masterDataRow = masterData.find(
    (masterDataObj) => masterDataObj[ID1] === currentRow[ID1],
  ) as IEditTableData;
  // check if comment is changed
  isCommentsChanged = currentRow[COMMENTS1 as keyof typeof currentRow] !== masterDataRow[COMMENTS1];
  // check if country name is empty when IN_SCOPE
  const isCountryEmptyWhenInScope = checkCountryEmptyWhenInScope(
    isScopeTableSelected,
    currentRow,
    SCOPE1,
  );

  currentRow = makeCurrentRowScopeToNumeric(isScopeTableSelected, currentRow, SCOPE1);
  const isAnyEditableColChangedExceptComments = isColChangedExceptComments(
    state,
    QTY1,
    currentRow,
    COMMENTS1,
    masterDataRow,
  );
  isCurrentRowErrored = checkIfCurrentRowErrored(
    isAnyEditableColChangedExceptComments,
    isCommentsChanged,
    isCommentsEmpty,
    isQtyNotvalid,
    isDealQtyNotvalid,
    isCountryEmptyWhenInScope,
  );
  for (let i = 0; i < errorRows.length; i++) {
    if (errorRows[i][ID1] === currentRow[ID1 as keyof typeof currentRow]) {
      currentRowIndexInErrorData = i;
      isCurrentRowExistsInErrorData = true;
      break;
    }
  }
  // if its an errored row and ID already present in Error data then, replace the old row with current incoming Row
  updateExistingRowData(
    isCurrentRowErrored,
    isCurrentRowExistsInErrorData,
    errorRows,
    currentRowIndexInErrorData,
    currentRow,
    dispatch,
  );

  // if its an errored row and ID is not in Error data, push this as new row
  addNewErroredRowData(
    isCurrentRowErrored,
    isCurrentRowExistsInErrorData,
    errorRows,
    currentRow,
    masterDataRow,
    dispatch,
    state,
  );

  // if its NOT an errored row and ID already present in Error data, remove the row from erroredRow
  removeExistingRowIfNotErrored(
    isCurrentRowErrored,
    isCurrentRowExistsInErrorData,
    errorRows,
    currentRowIndexInErrorData,
    dispatch,
  );
};

export const qtyCheckForThreeWholeDigits = (
  columnName: string,
  QTY1: string,
  DEAL_QUANTITY1: string,
  newDirtyData: IEditDirtyData[],
  setIsQtyExceedThreeWholeDigits: Dispatch<SetStateAction<boolean>>,
) => {
  // soft check for whole number having more than 3 characters
  let qtyLength = 0;
  let doesAnyQtyExceedThreeWholeDigits = false;
  if (areStringsEqual(columnName, QTY1) || areStringsEqual(columnName, DEAL_QUANTITY1)) {
    for (let i = 0; i < newDirtyData.length && columnName in newDirtyData[i]; i++) {
      qtyLength =
        newDirtyData[i][columnName as keyof IEditDirtyData]?.toString().split('.')[0].length ?? 0;
      if (qtyLength > THREE) {
        doesAnyQtyExceedThreeWholeDigits = true;
        break;
      }
    }
    if (doesAnyQtyExceedThreeWholeDigits) {
      setIsQtyExceedThreeWholeDigits(true);
    } else {
      setIsQtyExceedThreeWholeDigits(false);
    }
  }
};

export const addFirstRecord = (data: {
  columnName: string;
  COMMENTS1: string;
  isScopeTableSelected: boolean;
  options: InewRecordOptions;
  value: IEditTableData;
  countryNameAndCodeAmericas: ICountry[];
  SCOPE1: string;
  newDirtyData: IEditDirtyData[];
  publishData: IEditTableData[];
}) => {
  const {
    columnName,
    COMMENTS1,
    isScopeTableSelected,
    options,
    value,
    countryNameAndCodeAmericas,
    SCOPE1,
    newDirtyData,
    publishData,
  } = data;
  if (!areStringsEqual(columnName, COMMENTS1)) {
    // update country code and country name for changes in scope tables
    if (isScopeTableSelected) {
      options.newDirtyDataObj = insertCountryDetailsToDirtyData(
        options.newDirtyDataObj,
        value,
        columnName,
        countryNameAndCodeAmericas,
        SCOPE1,
      );
    }
    //  A First Entry is done when an editable field is changed, not when COMMENTS col is changed first
    newDirtyData.push(options.newDirtyDataObj);
    publishData.push(value);
  }
  return [newDirtyData, publishData];
};

// insertCountryDetailsToDirtyData function is to update dirty data with country name and country code automatically.
// the function works only for new entry in dirty data, not to update existing record in dirty data.
export const insertCountryDetailsToDirtyData = (
  currDirtyRow: IEditDirtyData,
  row: IEditTableData,
  column: string,
  countryNameAndCodeAmericas: ICountry[],
  SCOPE1: string,
) => {
  const updatedDirtyData = currDirtyRow;
  // if Country name is changed, update dirty data with Country Code
  if (areStringsEqual(column, COUNTRY_NAME)) {
    updatedDirtyData[COUNTRY_CODE] = getCountryCode(
      row[COUNTRY_NAME] as string,
      countryNameAndCodeAmericas,
    );
  }

  // if scope is changed to 0, update dirty data with Country Name and Country Code as empty values
  if (areStringsEqual(column, SCOPE1) && (row[SCOPE1] === 0 || row[SCOPE1] === OUT_OF_SCOPE)) {
    updatedDirtyData[COUNTRY_CODE] = getCountryCode('', countryNameAndCodeAmericas);
    updatedDirtyData[COUNTRY_NAME] = '';
  }

  // if scope is changed to 1, update dirty data with Country Name and Country Code values
  if (areStringsEqual(column, SCOPE1) && (row[SCOPE1] === 1 || row[SCOPE1] === IN_SCOPE)) {
    updatedDirtyData[COUNTRY_CODE] = getCountryCode(
      row[COUNTRY_NAME] as string,
      countryNameAndCodeAmericas,
    );
    updatedDirtyData[COUNTRY_NAME] = row[COUNTRY_NAME] as string;
  }
  return updatedDirtyData;
};
export const addNewRecord = (data: {
  columnName: string;
  COMMENTS1: string;
  isScopeTableSelected?: boolean;
  options: InewRecordOptions;
  value: IEditTableData;
  countryNameAndCodeAmericas: ICountry[];
  SCOPE1: string;
  newDirtyData: IEditDirtyData[];
  publishData: IEditTableData[];
}) => {
  const {
    columnName,
    COMMENTS1,
    isScopeTableSelected,
    newDirtyData,
    publishData,
    value,
    countryNameAndCodeAmericas,
    SCOPE1,
  } = data;
  if (columnName !== COMMENTS1) {
    // update country code and country name for changes in scope tables
    if (isScopeTableSelected) {
      data.options.newDirtyDataObj = insertCountryDetailsToDirtyData(
        data.options.newDirtyDataObj,
        value,
        columnName,
        countryNameAndCodeAmericas,
        SCOPE1,
      );
    }
    //  A new Entry is done when an editable field is changed, not when COMMENTS col is changed first
    newDirtyData?.push(data.options.newDirtyDataObj);
    publishData?.push(value);
  }
  return [newDirtyData, publishData];
};
export const updateRecordWithRevertedChange = (data: {
  stateVars: {
    setIsQtyExceedThreeWholeDigits: Dispatch<SetStateAction<boolean>>;
    dispatch: Dispatch<Action>;
    masterData: IEditTableData[];
    state: IEditFeatureDataState;
  };
  ID1: string;
  obj: IEditDirtyData;
  columnName: string;
  value: IEditTableData;
  isScopeTableSelected: boolean;
  SCOPE1: string;
  QTY1: string;
  publishData: IEditTableData[];
  currIndex: number;
  countryNameAndCodeAmericas: ICountry[];
  nonDirtyRow: boolean;
  COMMENTS1: string;
  newDirtyData: IEditDirtyData[];
}) => {
  const {
    obj,
    columnName,
    publishData,
    currIndex,
    value,
    isScopeTableSelected,
    SCOPE1,
    ID1,
    COMMENTS1,
    newDirtyData,
  } = data;
  // else if updated value matches with master data, remove the key from dirty data
  delete obj[columnName];
  publishData[currIndex] = value;
  // delete country details for scope table if same as master row
  if (isScopeTableSelected && columnName === SCOPE1) {
    delete obj[COUNTRY_NAME];
    delete obj[COUNTRY_CODE];
  } else if (isScopeTableSelected && columnName === COUNTRY_NAME) {
    delete obj[COUNTRY_CODE];
  }
  // if the object has no updated columns, deleted the entire object from dirty data
  //  OR the dirty object just has ID and COMMENTS columns, delete that entry
  const isFirstIndexAvailableAsId = Object.keys(obj).length === 1 && Object.keys(obj)[0] === ID1;
  const isSecondIndexAvailableAsComments =
    Object.keys(obj).length === TWO &&
    Object.keys(obj)[0] === ID1 &&
    Object.keys(obj)[1] === COMMENTS1;
  data.nonDirtyRow = isFirstIndexAvailableAsId || isSecondIndexAvailableAsComments;
  // during validation error mode, remove the row from dirtyData if all changes are reverted
  if (data.nonDirtyRow) {
    newDirtyData.splice(currIndex, 1);
    publishData.splice(currIndex, 1);
  }
  return [newDirtyData, publishData];
};
export const getCountryCode = (countryName: string, countryNameAndCodeAmericas: ICountry[]) => {
  let countryCode = '';
  for (const key of countryNameAndCodeAmericas) {
    if (key.COUNTRY_NAME === countryName) {
      countryCode = key.COUNTRY_CODE;
      break;
    }
  }
  return countryCode;
};
export const updateDataWithNewValue = (data: {
  obj: IEditDirtyData;
  columnName: string;
  value: IEditTableData;
  publishData: IEditTableData[];
  currIndex: number;
  isScopeTableSelected: boolean;
  SCOPE1: string;
  countryNameAndCodeAmericas: ICountry[];
}) => {
  const {
    obj,
    columnName,
    value,
    publishData,
    currIndex,
    isScopeTableSelected,
    SCOPE1,
    countryNameAndCodeAmericas,
  } = data;
  // update or add column with new column name
  obj[columnName] = value[columnName];
  publishData[currIndex] = value;
  // updating country details for scope table
  if (isScopeTableSelected && columnName === SCOPE1) {
    obj[COUNTRY_NAME as keyof typeof obj] = value[COUNTRY_NAME];
    obj[COUNTRY_CODE] = getCountryCode(value[COUNTRY_NAME] as string, countryNameAndCodeAmericas);
  } else if (isScopeTableSelected && columnName === COUNTRY_NAME) {
    obj[COUNTRY_CODE] = getCountryCode(value[COUNTRY_NAME] as string, countryNameAndCodeAmericas);
  }
};
export const setCurrCellValue = (
  isScopeTableSelected: boolean,
  columnName: string,
  SCOPE1: string,
  QTY1: string,
  actualValue: string | number,
) => {
  // if scope is changed for an existing record, convert scope value from string to number
  const scope1tableSelected = isScopeTableSelected && columnName === SCOPE1 && typeof actualValue === 'string';
  if (scope1tableSelected) {
    return Number(actualValue.slice(0, 1));
  } else if (columnName === QTY1) {
    return Number(actualValue);
  } else {
    return actualValue;
  }
};
export const updateExistingRecord = (data: {
  stateVars: {
    setIsQtyExceedThreeWholeDigits: Dispatch<SetStateAction<boolean>>;
    dispatch: Dispatch<Action>;
    masterData: IEditTableData[];
    state: IEditFeatureDataState;
  };
  ID1: string;
  obj: IEditDirtyData;
  columnName: string;
  value: IEditTableData;
  isScopeTableSelected: boolean;
  SCOPE1: string;
  QTY1: string;
  publishData: IEditTableData[];
  currIndex: number;
  countryNameAndCodeAmericas: ICountry[];
  nonDirtyRow: boolean;
  COMMENTS1: string;
  newDirtyData: IEditDirtyData[];
}) => {
  const {
    stateVars,
    ID1,
    obj,
    columnName,
    value,
    isScopeTableSelected,
    SCOPE1,
    QTY1,
    publishData,
    currIndex,
    countryNameAndCodeAmericas,
    newDirtyData,
  } = data;
  // compare the updated value with master value
  for (const masterDataObj of stateVars.masterData) {
    if (masterDataObj[ID1 as keyof typeof masterDataObj] === obj[ID1 as keyof typeof obj]) {
      const masterColVal = masterDataObj[columnName as keyof typeof masterDataObj];
      const valueColVal = setCurrCellValue(
        isScopeTableSelected,
        columnName,
        SCOPE1,
        QTY1,
        value[columnName],
      );
      // if updated value is not equal to master value, then update the dirty data
      if (masterColVal !== valueColVal) {
        const newValueData = {
          obj,
          columnName,
          value,
          publishData,
          currIndex,
          isScopeTableSelected,
          SCOPE1,
          countryNameAndCodeAmericas,
        };
        updateDataWithNewValue(newValueData);
      } else {
        const [updatedNewDirtyData, updatedPublishData] = updateRecordWithRevertedChange(data);
        data.newDirtyData = updatedNewDirtyData;
        data.publishData = updatedPublishData as IEditTableData[];
      }
    }
  }
  return [newDirtyData, publishData];
};

export const setDirtyDataAndPublishData = (options: InewRecordOptions) => {
  const {
    newDirtyData,
    ID1,
    SCOPE1,
    COMMENTS1,
    QTY1,
    value,
    stateVars,
    columnName,
    isScopeTableSelected,
    publishData,
    countryNameAndCodeAmericas,
  } = options;
  const data = {
    columnName,
    COMMENTS1,
    isScopeTableSelected,
    options,
    value,
    countryNameAndCodeAmericas,
    SCOPE1,
    newDirtyData,
    publishData,
  };
  if (newDirtyData.length > 0) {
    let currIndex = 0;

    // iterate the array
    for (const obj of newDirtyData) {
      currIndex = newDirtyData.indexOf(obj);
      const nonDirtyRow = false;
      // check if ID (I_Aligne_Id) is already present
      if (obj[ID1] === value[ID1]) {
        const param = {
          stateVars,
          ID1,
          obj,
          columnName,
          value,
          isScopeTableSelected,
          SCOPE1,
          QTY1,
          publishData,
          currIndex,
          countryNameAndCodeAmericas,
          nonDirtyRow,
          COMMENTS1,
          newDirtyData,
        };
        const [updatedDirtyData, updatedPublish] = updateExistingRecord(param);
        return [updatedDirtyData, updatedPublish];
      }
    }
    //  for new ID (I_Aligne_Id)
    const [updatedNewDirtyData, updatedPublishData] = addNewRecord(data);
    return [updatedNewDirtyData, updatedPublishData];
  } else {
    const [updatedNewDirtyData, updatedPublishData] = addFirstRecord(data);
    return [updatedNewDirtyData, updatedPublishData];
  }
};

export const setDirtyDataObj = (
  stateVars: {
    setIsQtyExceedThreeWholeDigits?: Dispatch<SetStateAction<boolean>>;
    dispatch?: Dispatch<Action>;
    masterData: IEditTableData[];
    state?: IEditFeatureDataState;
  },
  ID1: string,
  COMMENTS1: string,
  value: IEditTableData,
  columnName: string,
) => {
  let result: IEditDirtyData = {};
  for (const masterDataObj of stateVars.masterData) {
    const comment = masterDataObj[COMMENTS1 as keyof typeof masterDataObj] ?? '';
    const valueComment = value[COMMENTS1 as keyof typeof value] ?? '';
    if (masterDataObj[ID1 as keyof typeof masterDataObj] === value[ID1 as keyof typeof value]) {
      result = {
        [ID1]: value[ID1 as keyof typeof value],
        [columnName]: value[columnName as keyof typeof value],
      };
      if (!isCommentsError(comment.toString(), valueComment.toString())) {
        result[COMMENTS1 as keyof typeof result] = valueComment;
      }
    }
  }
  return result;
};

export const maintainDirtyData = (
  colData: { columnName: string; value: IEditTableData; countryNameAndCodeAmericas: ICountry[] },
  colNames: {
    COMMENTS1: string;
    ID1: string;
    QTY1: string;
    SCOPE1: string;
    DEAL_QUANTITY1: string;
  },
  booleanVars: { isEditError: boolean; isScopeTableSelected: boolean },
  stateVars: {
    setIsQtyExceedThreeWholeDigits: Dispatch<SetStateAction<boolean>>;
    dispatch: Dispatch<Action>;
    masterData: IEditTableData[];
    state: IEditFeatureDataState;
  },
) => {
  const { columnName, value, countryNameAndCodeAmericas } = colData;
  const { COMMENTS1, ID1, QTY1, SCOPE1, DEAL_QUANTITY1 } = colNames;
  const { isEditError, isScopeTableSelected } = booleanVars;
  const { setIsQtyExceedThreeWholeDigits, dispatch, state } = stateVars;
  const newDirtyData = [...state.dirtyData];
  const publishData = [...state.publishRows];
  // in case data is already in master Data but not in dirty data.
  // It happens when a dirty data is reverted to actual with COMMENTS being different
  const newDirtyDataObj = setDirtyDataObj(stateVars, ID1, COMMENTS1, value, columnName);
  if (isEditError) {
    // during validation error, masterErroredRows is the master data
    stateVars.masterData = [...state.masterErroredRows];
  } else {
    stateVars.masterData = [...state.masterPaginationData];
  }
  const options = {
    newDirtyData,
    ID1,
    SCOPE1,
    COMMENTS1,
    QTY1,
    value,
    stateVars,
    columnName,
    isScopeTableSelected,
    publishData,
    countryNameAndCodeAmericas,
    newDirtyDataObj,
  };
  const [updatedNewDirtyData, updatedPublishData] = setDirtyDataAndPublishData(options);

  dispatch({ type: 'setDirtyData', payload: updatedNewDirtyData });
  dispatch({ type: 'setPublishRows', payload: updatedPublishData });
  qtyCheckForThreeWholeDigits(
    columnName,
    QTY1,
    DEAL_QUANTITY1,
    newDirtyData,
    setIsQtyExceedThreeWholeDigits,
  );
};

export const handleEditForQtyAndDealQty = (data: {
  e: React.ChangeEvent<HTMLSelectElement> | string | number;
  row: IEditTableData;
  columnName: string;
  QTY1: string;
  DEAL_QUANTITY1: string;
  setUpdatedCol: Dispatch<SetStateAction<string>>;
  masterData: IEditTableData[];
  ID1: string;
  setOriginalVal: Dispatch<SetStateAction<string | number>>;
  setUpdatedVal: Dispatch<SetStateAction<string | number>>;
}) => {
  const {
    e,
    row,
    columnName,
    QTY1,
    DEAL_QUANTITY1,
    setUpdatedCol,
    masterData,
    ID1,
    setOriginalVal,
    setUpdatedVal,
  } = data;
  if (areStringsEqual(columnName, QTY1) || areStringsEqual(columnName, DEAL_QUANTITY1)) {
    let masterVal: string | number = '';
    setUpdatedCol(columnName);
    for (const masterDataObj of masterData) {
      if (masterDataObj[ID1] === row[ID1]) {
        masterVal = masterDataObj[columnName];
      }
    }
    setOriginalVal(masterVal);
    let newVal: string | number;
    if (typeof e === 'string' || typeof e === 'number') {
      newVal = e;
    } else {
      newVal = e.target.value;
    }
    if (masterVal === newVal) {
      setUpdatedVal(NOT_UPDATED);
    } else {
      setUpdatedVal(newVal);
    }
  }
};

export const getData = (
  isEditError: boolean,
  errorTableData: IEditTableData[],
  state: IEditFeatureDataState,
) => {
  let currPageData;
  let masterData;

  if (isEditError) {
    currPageData = [...errorTableData];
    masterData = [...state.masterErroredRows];
  } else {
    currPageData = [...state.viewEditPaginationData];
    masterData = [...state.masterPaginationData];
  }
  return { currPageData, masterData };
};
// handleEditData is called whenever a data is changed
export const handleEditData = (
  colData: {
    e: React.ChangeEvent<HTMLSelectElement> | string | number;
    val: string;
    index: number;
    row: IEditTableData;
  },
  booleanVar: { isEditError: boolean; isScopeTableSelected: boolean },
  colNames: string[],
  stateVars: {
    state: IEditFeatureDataState;
    setIsDirtyData: Dispatch<SetStateAction<boolean>>;
    dispatch: Dispatch<Action>;
    setUpdatedVal: Dispatch<SetStateAction<string | number>>;
    setOriginalVal: Dispatch<SetStateAction<string | number>>;
    setUpdatedCol: Dispatch<SetStateAction<string>>;
    countryNameAndCodeAmericas: ICountry[];
    setIsQtyExceedThreeWholeDigits: Dispatch<SetStateAction<boolean>>;
    errorTableData: IEditTableData[];
  },
) => {
  const { e, val, index, row } = colData;
  const { isEditError, isScopeTableSelected } = booleanVar;
  const [ID1, QTY1, DEAL_QUANTITY1, , , , COMMENTS1, SCOPE1] = colNames;
  const {
    state,
    setIsDirtyData,
    dispatch,
    setUpdatedVal,
    setOriginalVal,
    setUpdatedCol,
    countryNameAndCodeAmericas,
    setIsQtyExceedThreeWholeDigits,
    errorTableData,
  } = stateVars;
  const columnName: string = val;
  setIsDirtyData(true);
  //  currPageData is the data being shown currently on the page. during validation error, the current data is errored data
  const { currPageData, masterData } = getData(isEditError, errorTableData, state);
  const info = {
    e,
    row,
    columnName,
    QTY1,
    DEAL_QUANTITY1,
    setUpdatedCol,
    masterData,
    ID1,
    setOriginalVal,
    setUpdatedVal,
  };
  handleEditForQtyAndDealQty(info);
  const updatedPageData = currPageData.map((value, ind: number) => {
    if (ind === index) {
      if (typeof e === 'string' || typeof e === 'number') {
        value[columnName] = e;
      } else {
        value[columnName] = e.target.value;
      }
      const columnData = { columnName, value, countryNameAndCodeAmericas };
      const columnNames = { COMMENTS1, ID1, QTY1, SCOPE1, DEAL_QUANTITY1 };
      const booleanVars = { isEditError, isScopeTableSelected };
      const stateVar = {
        setIsQtyExceedThreeWholeDigits,
        dispatch,
        masterData,
        state,
      };
      // maintain dirty data
      maintainDirtyData(columnData, columnNames, booleanVars, stateVar);

      // maintain error data
      maintainErrorData(
        value,
        state,
        isEditError,
        isScopeTableSelected,
        { COMMENTS1, QTY1, DEAL_QUANTITY1, ID1, SCOPE1 },
        dispatch,
      );
    }
    return value;
  });
  dispatch({ type: 'setViewEditPaginationData', payload: updatedPageData });
};